import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Container from '@/src/common/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import { ProductCardSipjoy } from '@/components/ProductCard'
import Loading from '@/components/Loading';
import Link from 'next/link'
import Head from 'next/head'
import { useRouter } from 'next/router';
import { Mixpanel } from '@/utils/mixpanel';
import YellowCouponBanner from '@/views/landingPage/YellowCouponBanner';
import Grow from '@mui/material/Fade';
import {Link as MuiLink }from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import PurchaseSelector from '@/components/PurchaseSelector';
import { canProducerShipToSelectedState, getSelectedStateName } from '@/utils/statesHelper';
import { DRINKAMIE } from '@/consts/siteConsts'

import { getProducerLocationStr } from '@/utils/location';
import Meta  from '@/src/components/Meta';
import {getMinPricePerBottle} from '@/utils/priceCalc';
import PricingSaving from './components/PricingSaving';
import NotFound from '@/views/supportingPages/NotFound';
import ProducerAbout from '@/views/producerPage/ProducerAbout';
import ProducerGallery from '@/views/producerPage/ProducerGallery';
import { makeProductUrl } from '@/utils/urlGenerator';
import Affiliate from '@/utils/affiliate';
import ProductBasicInfo from './ProductBasicInfo';
import NarrowContainer from '@/src/common/NarrowContainer';
import Image from 'next/image'
import MarkdownView from '@/src/components/Markdown/MarkdownView';
import SearchTopicCard from '../../SearchTopics/SearchTopicCard';
import { generateCategoriesForProduct } from './productRelatedCategories';
import { useSite } from '@/src/contexts/SiteContext';
import { brandContainMedia } from '../producerPage/ProducerPage';
import { Faq } from '../..';
import ImageGallery from './ImageGallery';
import SitePageComponent from '../../SitePage/SitePageComponent';

const GENERIC_HERO_IMAGE =
  'https://images.pexels.com/photos/357742/pexels-photo-357742.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
const RELATED_PRODUCT_CARD_REF = 'related_product';

const ProductPage = ({pageData}) => {
  const router = useRouter();
  const theme = useTheme();
  const { site } = useSite()
  const mainColor = theme.palette.primary.main;
  const alternateBackground = theme.palette.background.level1;
  const [errorLoading, setErrorLoading] = useState();
  const [product, setProduct] = useState();
  const [relatedProducts] = useState(pageData?.relatedProducts);
  const [affiliateData, setAffiliateData] = useState();

  const pageTemplate = pageData?.product?.page_template;
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });

  let { productKey , slug } = router.query;
  productKey = (productKey || '').toLowerCase();
 
  const brand = product?.brand;
  const wineryName = brand?.name;
  const subtitle = brand?.title;
  const locationStr = brand && getProducerLocationStr(brand);

  const brandLink = brand?.slug ? `/brand/${brand?.slug}?ref=product_page` : null;
  
  const brandContainInfo = (brand) => {
    if (!brand) {
      return false;
    }
    if (brand?.about) {
      return true;
    }
    if (brandContainMedia(brand)) {
      return true;
    }
    return false;
  };


  useEffect(() => {
    if(!router.isReady) return;

    if (pageData?.product) {
      setProduct(pageData.product)
    }
    
    Mixpanel.track('Product page', {
      productKey: pageData?.product?.slug,
      slug
    });

    Affiliate.getAffiliateData((affiliateData) => {
      if (!affiliateData) {
        console.error('Couldn\'t load affiliate recommendations');
      } else {
        let coupon = affiliateData?.coupon;
        if (coupon?.limitedToProducers) {
          const validForThisProduct = coupon.limitedToProducers.some((p) => p.key === producer?.key);
          if (!validForThisProduct) {
            coupon = null;
            affiliateData = null;
          }
        }
        setAffiliateData(affiliateData);
      }
    });

    // we do not load the data from the client over here because the server is building
    // a page with data
    // if (!product) {
    //   loadPageInfo();
    // } else if (!relatedProducts){
    //   loadRelatedProducts(product);
    // }
  }, [router.asPath, router.isReady]);
 

  if (errorLoading) {
    return (<NotFound/>);
  }


  

  const validState = canProducerShipToSelectedState(brand);

  const minPriceInCents = getMinPricePerBottle(product);
  const discoverCategories = generateCategoriesForProduct({product, minPriceInCents});

  let discountPercentage;
  if (affiliateData?.coupon?.discountPercentage) {
    discountPercentage = parseInt(affiliateData.coupon.discountPercentage);
  }



  if (!product) {
    return <Loading/>
  }

  const content = product?.content;
  const productName = content?.title;
  const productShortDesc = content?.short_description;
  const productDesc = content?.description;
  const productImage = content?.main_image;
  const productUrl = `https://sipjoy.shop/product/${slug}`;
  const images = content.media
    ? [productImage, ...content.media]
    : [productImage]
  const nameComp = (
    <>
      {product?.hidden && (
        <Typography
          gutterBottom
          align="center"
          sx={{
            color: 'red',
            fontWeight: 'medium',
            borderBottom: 'dotted red',
            borderTop: 'dotted red',
            paddingBottom: '2px',
          }}
        >
          hidden on main site
        </Typography>
      )}
      <Typography
        variant="h3"
        align="center"
        sx={{  fontWeight: 200, fontSize: '55px !important'}}
        color={theme.palette.primary.main}
        component="p"
      >
        {productName}
      </Typography>
      
      {productShortDesc ? (
        <Box sx={{ padding: '15px 0' }}>
          <MarkdownView>{productShortDesc}</MarkdownView>
        </Box>
      ): null}
    </>
  );

  const descriptionComp = (productDesc) && (
    <Box >
      {productDesc ? (
        <MarkdownView>{productDesc}</MarkdownView>
      ): null}
    </Box>
  );
  console.log({descriptionComp, productShortDesc , productDesc});
  const seo = product?.content?.seo;

  const hideCheckOutCTA = site?.key === DRINKAMIE ? true : false

  return (
    <Box>
      <Meta
        site={site}
        title={`${seo?.title || productName}`}
        description={seo?.description || productShortDesc}
        previewImage={seo?.preview_image?.src || productImage}
        keywords={seo?.keywords}
        url={productUrl}
      />

      {validState === false && (
        <Alert severity="error">
          <Typography sx={{ fontWeight: '700' }}>
            This winery do NOT ship to {getSelectedStateName()}{' '}
          </Typography>
        </Alert>
      )}

      {affiliateData !== undefined && affiliateData && affiliateData?.coupon && (
        <Grow in={product ? true : false} style={{ timeout: 5000 }}>
          <Box boxShadow={2} margin="20px 0">
            <YellowCouponBanner
              bannerText={affiliateData?.couponBannerText}
              coupon={affiliateData?.coupon}
            />
          </Box>
        </Grow>
      )}

      <Container>
        <Box
          display="flex"
          flexDirection={isMd ? 'row' : 'column'}
          gap={5}
          justifyContent={'space-between'}
        >
          {images.length > 0 && (
            <Box sx={{ position: 'relative', width: '100%', height: '500px' }}>
              {images.length > 1 ? (
                <ImageGallery images={images} height={'400px'} />
              ) : (
                <Image
                  layout={'fill'}
                  objectFit={'contain'}
                  src={productImage}
                />
              )}
            </Box>
          )}

          <Box
            display="flex"
            flexDirection="column"
            alignItems="strtch"
            sx={{ minWidth: isMd ? '400px' : '300px' }}
          >
            {nameComp}

            {!hideCheckOutCTA && (
              <Box marginTop={5}>
                <PricingSaving
                  product={product}
                  minPriceInCents={minPriceInCents}
                  discountPercentage={discountPercentage}
                />
                <PurchaseSelector
                  product={product}
                  discountPercentage={discountPercentage}
                />
              </Box>
            )}
          </Box>
        </Box>
        {descriptionComp && (
          <Grid item xs={12} sx={{ padding: '50px 15px 10px 15px' }}>
            <ProductBasicInfo product={product} textAlign={'left'} />
            {descriptionComp}
          </Grid>
        )}
      </Container>

      {brandContainInfo(brand) && (
        <Box backgroundColor={alternateBackground}>
          <Container padding="30px 50px !important">
            <Box fullWidth display="flex" justifyContent="space-evenly">
              <Link href={brandLink}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700 }}
                  gutterBottom
                  color={theme.palette.primary.main}
                  align={'center'}
                >
                  {`About ${brand.name}`}
                </Typography>
              </Link>
            </Box>
            <Box
              marginTop={3}
              display="flex"
              flexDirection={isMd ? 'row' : 'column'}
              gap={2}
            >
              <Box width={isMd && brandContainMedia(brand) ? '50%' : '100%'}>
                <ProducerAbout producer={brand} />
              </Box>
              {brandContainMedia(brand) && (
                <Box
                  width={isMd ? '50%' : '100%'}
                  display="flex"
                  justifyContent="center"
                >
                  <ProducerGallery producer={brand} />
                </Box>
              )}
            </Box>
          </Container>
        </Box>
      )}

      {relatedProducts && relatedProducts.length > 0 && (
        <Box bgcolor={mainColor} position={'relative'}>
          <Container paddingTop="30px !important">
            <Typography
              variant="h5"
              sx={{
                textTransform: 'uppercase',
                fontWeight: 'medium',
              }}
              gutterBottom
              color={theme.palette.primary.contrastText}
              align={'center'}
              component="p"
            >
              You may also like
            </Typography>

            <Grid container spacing={2} marginTop={3}>
              {relatedProducts.map((product, i) => (
                <Grid item xs={12} sm={6} md={4} key={i} data-aos={'fade-up'}>
                  <ProductCardSipjoy
                    producerKey={producer?.key}
                    product={product}
                    locRef={RELATED_PRODUCT_CARD_REF}
                    showSourceInsteadDetails={true}
                  />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}
      {product?.productCategory && (
        <Box bgcolor={alternateBackground} position={'relative'}>
          <Container paddingTop="30px !important">
            <Typography
              variant="h5"
              sx={{
                textTransform: 'uppercase',
                fontWeight: 'medium',
              }}
              gutterBottom
              color={mainColor}
              align={'center'}
              component="p"
            >
              Discover more {product.productCategory}
            </Typography>

            {discoverCategories && discoverCategories.length > 0 && (
              <Grid container spacing={2} marginTop={3}>
                {discoverCategories.map((item, key) => {
                  return (
                    <SearchTopicCard key={key} item={item} url={item.url} />
                  )
                })}
              </Grid>
            )}
          </Container>
        </Box>
      )}

      {pageTemplate?.body ? (
        <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
          {(pageTemplate.body || []).map((item, key) => {
            return (
              <Box
                key={key}
                width="100%"
                sx={{
                  backgroundColor:
                    item.backgroundColor ||
                    (key % 2 === 1 && alternateBackground),
                }}
              >
                <NarrowContainer position={'relative'} top={0}>
                  <SitePageComponent item={item} />
                </NarrowContainer>
              </Box>
            )
          })}
        </Box>
      ) : null}
    </Box>
  )
};

export default ProductPage;
